import { ApiError, appelerApi, lireErreur, ReseauError } from './apiErrors'

/**
 * Connexion à l'administration (LexikJWTAuthenticationBundle, POST /api/login).
 * Le jeton est gardé dans le navigateur ; il porte sa propre date d'expiration.
 */
export const API_URL = import.meta.env.VITE_API_URL?.replace(/\/+$/, '') || undefined

/** Sans API configurée, l'admin fonctionne en mode démonstration (pas de connexion). */
export const MODE_DEMO = !API_URL

const TOKEN_KEY = 'cmp-admin-token'

interface JwtPayload {
  exp: number
  username?: string
  roles?: string[]
}

const decoder = (token: string): JwtPayload | null => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      [...atob(base64)].map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    )
    return JSON.parse(json) as JwtPayload
  } catch {
    return null
  }
}

/** Jeton valide (non expiré), ou null. Un jeton expiré est effacé. */
const lireToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export const getToken = (): string | null => {
  const token = lireToken()
  if (!token) return null
  const payload = decoder(token)
  // Marge de 30 s : on évite d'envoyer un jeton qui expirerait pendant la requête.
  if (!payload || payload.exp * 1000 < Date.now() + 30_000) {
    logout()
    return null
  }
  return token
}

/** E-mail de l'administrateur connecté (claim « username » du jeton). */
export const getUtilisateur = (): string | null => {
  const token = getToken()
  return token ? (decoder(token)?.username ?? null) : null
}

export const logout = () => {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    // Stockage indisponible : rien à effacer.
  }
}

/**
 * Connexion. En cas d'échec, lève une ApiError (message du back, traduit si besoin)
 * ou une ReseauError (serveur injoignable, délai dépassé, hors connexion).
 */
export async function login(email: string, password: string): Promise<void> {
  if (!API_URL) throw new ReseauError("L'adresse de l'API n'est pas configurée (VITE_API_URL).")

  const res = await appelerApi(`${API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email: email.trim(), password }),
  })
  if (!res.ok) throw await lireErreur(res)

  const { token } = (await res.json().catch(() => ({}))) as { token?: string }
  if (!token) throw new ApiError(res.status, 'Réponse inattendue du serveur : aucun jeton reçu.')
  localStorage.setItem(TOKEN_KEY, token)
}
